import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';

// Helper function to clean and validate base64 string
const cleanBase64String = (base64String) => {
  if (!base64String || typeof base64String !== 'string') {
    return null;
  }
  
  // Remove data URI prefix if present
  let cleaned = base64String.includes(',') 
    ? base64String.split(',')[1] 
    : base64String;
  
  // Remove all whitespace, newlines, and invalid characters
  cleaned = cleaned.replace(/\s/g, '').replace(/[^A-Za-z0-9+/=]/g, '');
  
  // Add padding if needed (base64 strings should be multiples of 4)
  while (cleaned.length % 4 !== 0) {
    cleaned += '=';
  }
  
  // Validate it's valid base64
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleaned)) {
    logger.warn('Invalid base64 string format');
    return null;
  }
  
  return cleaned;
};

const VideoWatermarkProgressModal = ({ isOpen, onClose, storyId, apiClient, storyTitle = 'story', user = null }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Starting video watermarking...');
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const hasDownloadedRef = useRef(false);
  const hasTrackedDownloadRef = useRef(false); // Track if DOWNLOAD_VIDEO tag has been added
  const progressRef = useRef(0);
  const videoChunksRef = useRef([]);

  useEffect(() => {
    if (!isOpen || !storyId || !apiClient) return;

    // Reset state when modal opens
    setProgress(0);
    setStatus('processing');
    setMessage('Connecting to server...');
    setError(null);
    hasDownloadedRef.current = false;
    hasTrackedDownloadRef.current = false; // Reset download tracking
    progressRef.current = 0;
    videoChunksRef.current = [];

    // Get the base URL from API client
    const baseURL = apiClient.baseURL;
    
    // Convert HTTP/HTTPS to WS/WSS
    const wsProtocol = baseURL.startsWith('https') ? 'wss' : 'ws';
    const wsBaseURL = baseURL.replace(/^https?:\/\//, '');
    
    // Get auth token
    const token = apiClient.token || localStorage.getItem('pixley_auth_token');
    
    // Create WebSocket URL
    const wsUrl = `${wsProtocol}://${wsBaseURL}/stories/${storyId}/download-watermarked-progress`;
    
    logger.log('🔌 Connecting to WebSocket:', wsUrl);

    // Create WebSocket connection
    // Note: WebSocket constructor doesn't support headers directly in browser
    // We'll need to pass token as query param or use a different approach
    const ws = new WebSocket(`${wsUrl}?token=${token || ''}`);

    wsRef.current = ws;

    ws.onopen = () => {
      logger.log('✅ WebSocket connection opened');
      setMessage('Connected. Starting video watermarking...');
    };

    // Helper function to process base64 chunk
    const processBase64Chunk = (base64Data) => {
      try {
        const cleaned = cleanBase64String(base64Data);
        if (!cleaned) {
          logger.warn('Invalid base64 string, skipping chunk');
          return false;
        }
        
        const binaryString = atob(cleaned);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        videoChunksRef.current.push(new Blob([bytes], { type: 'video/mp4' }));
        setMessage('Receiving video file...');
        return true;
      } catch (e) {
        logger.error('Error processing base64 chunk:', e);
        return false;
      }
    };

    // Helper function to handle progress updates
    const handleProgressUpdate = (data) => {
      logger.log('📊 Progress update:', data);

      if (data.type === 'video_chunk') {
        // Handle base64 video chunk from JSON
        logger.log('📦 Received video chunk (JSON base64)');
        const base64Data = data.chunk || data.data;
        
        if (!base64Data) {
          logger.warn('No chunk data in video_chunk message');
          return;
        }
        
        processBase64Chunk(base64Data);
      } else {
        // Regular progress update
        if (data.progress !== undefined) {
          setProgress(data.progress);
          progressRef.current = data.progress;
        }
        if (data.message) {
          setMessage(data.message);
        }
        if (data.status) {
          setStatus(data.status);
        }

        // When progress reaches 100%, prepare to download
        if (data.progress === 100 && data.status === 'complete' && !hasDownloadedRef.current) {
          hasDownloadedRef.current = true;
          setMessage('Video ready! Preparing download...');
          
          // Add DOWNLOAD_VIDEO tag to user's data_tags (only on successful download)
          if (user?.id && apiClient && storyId && !hasTrackedDownloadRef.current) {
            hasTrackedDownloadRef.current = true; // Prevent duplicate tags
            const timestamp = Date.now();
            const tag = `DOWNLOAD_VIDEO_${storyId}_${timestamp}`;
            apiClient.addUserDataTag(user.id, tag).then((tagResult) => {
              if (tagResult.status) {
                logger.log(`✅ Added download video tag ${tag} for user ${user.id}`);
              } else {
                logger.warn(`⚠️ Failed to add download video tag ${tag}:`, tagResult.error);
              }
            }).catch((error) => {
              logger.error(`❌ Error adding download video tag ${tag}:`, error);
            });
          }
          
          // Wait a moment for any remaining chunks, then download
          setTimeout(() => {
            downloadVideo();
          }, 500);
        }
      }
    };

    ws.onmessage = (event) => {
      try {
        // Check if it's binary data (video chunk) or text (progress update)
        if (event.data instanceof Blob) {
          // Handle binary video chunk
          logger.log('📦 Received video chunk (binary)');
          videoChunksRef.current.push(event.data);
          setMessage('Receiving video file...');
        } else if (event.data instanceof ArrayBuffer) {
          // Handle ArrayBuffer video chunk
          logger.log('📦 Received video chunk (ArrayBuffer)');
          videoChunksRef.current.push(new Blob([event.data]));
          setMessage('Receiving video file...');
        } else {
          // Handle text data (progress updates or base64 chunks)
          const textData = event.data;
          
          // Check if it's base64 encoded video chunk
          if (textData.startsWith('data:video/') || (textData.length > 1000 && !textData.trim().startsWith('{'))) {
            // Likely a base64 video chunk
            logger.log('📦 Received video chunk (base64 string)');
            const base64Data = textData.includes(',') 
              ? textData.split(',')[1] 
              : textData;
            
            if (!processBase64Chunk(base64Data)) {
              // If base64 decoding fails, try parsing as JSON
              try {
                const data = JSON.parse(textData);
                handleProgressUpdate(data);
              } catch (parseError) {
                logger.error('Could not parse as base64 or JSON:', parseError);
              }
            }
          } else {
            // Parse as JSON progress update
            try {
              const data = JSON.parse(textData);
              handleProgressUpdate(data);
            } catch (parseError) {
              logger.error('Could not parse as JSON:', parseError, 'Data:', textData.substring(0, 100));
            }
          }
        }
      } catch (error) {
        logger.error('Error processing WebSocket message:', error);
        // If it's not JSON and not binary, it might be an error message
        if (typeof event.data === 'string' && !event.data.includes('{') && !event.data.startsWith('data:')) {
          setError(event.data);
          setStatus('error');
        }
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error:', error);
      setError('Connection error. Please try again.');
      setStatus('error');
    };

    ws.onclose = (event) => {
      logger.log('WebSocket closed:', event.code, event.reason);
      
      // If we reached 100% and haven't downloaded yet, try to download
      if (progressRef.current === 100 && !hasDownloadedRef.current && videoChunksRef.current.length > 0) {
        hasDownloadedRef.current = true;
        downloadVideo();
      } else if (progressRef.current < 100 && !hasDownloadedRef.current) {
        if (event.code !== 1000) { // 1000 is normal closure
          setError('Connection closed unexpectedly. Please try again.');
          setStatus('error');
        }
      }
    };

    // Download function
    const downloadVideo = () => {
      try {
        // If we have video chunks, reconstruct the video
        if (videoChunksRef.current.length > 0) {
          logger.log(`📦 Reconstructing video from ${videoChunksRef.current.length} chunks`);
          const videoBlob = new Blob(videoChunksRef.current, { type: 'video/mp4' });

          // Sanitize story title for filename (remove special characters, limit length)
          const sanitizedTitle = (storyTitle || 'story')
            .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special chars
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .substring(0, 50) // Limit length
            .toLowerCase();

          // Create download link
          const downloadUrl = window.URL.createObjectURL(videoBlob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `${sanitizedTitle}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
          
          logger.log('✅ Video downloaded successfully');
          setMessage('Download complete!');
          setStatus('complete');
          
          setTimeout(() => {
            onClose();
          }, 1500);
        } else {
          // Fallback: use the regular download endpoint
          logger.log('📥 No chunks received, using fallback download endpoint');
          apiClient.downloadWatermarkedStory(storyId).then((result) => {
            if (result.success) {
              setMessage('Download complete!');
              setTimeout(() => {
                onClose();
              }, 1500);
            } else {
              setError(result.error || 'Failed to download video');
              setStatus('error');
            }
          }).catch((downloadError) => {
            logger.error('Error downloading video:', downloadError);
            setError('Failed to download video. Please try again.');
            setStatus('error');
          });
        }
      } catch (error) {
        logger.error('Error in downloadVideo:', error);
        setError('Failed to prepare video download. Please try again.');
        setStatus('error');
      }
    };

    // Cleanup on unmount or when modal closes
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, storyId, apiClient]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      // Don't allow closing while processing
      if (status === 'processing' && progress < 100) {
        return;
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleBackdropClick}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-md w-full p-8"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                background: '#FFFFFF',
                border: '2px solid rgba(11,26,51,0.06)',
                borderRadius: 28,
                boxShadow: '0px 8px 0px rgba(11,26,51,0.06), 0px 28px 64px rgba(11,26,51,0.28)',
              }}
            >
              {/* Panda hard at work */}
              <div className="flex justify-center mb-2">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-28 h-28 object-contain"
                  aria-hidden
                >
                  <source src="/assets/loading-panda.webm" type="video/webm" />
                  <source src="/assets/loading-panda.mp4" type="video/mp4" />
                </video>
              </div>

              {/* Header */}
              <div className="text-center mb-6">
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#0065F8',
                  }}
                >
                  Almost there
                </span>
                <h2
                  className="mt-1 mb-2"
                  style={{ fontSize: 26, fontWeight: 700, color: '#0B1A33', lineHeight: 1.15 }}
                >
                  Preparing Your Video
                </h2>
                <p style={{ fontSize: 14, color: '#5A6B85' }}>
                  Adding the Pixley touch to your story — this can take a few minutes.
                </p>
              </div>

              {/* Progress Section */}
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="relative">
                  <div
                    className="w-full overflow-hidden"
                    style={{ height: 14, background: 'rgba(0,101,248,0.10)', borderRadius: 999 }}
                  >
                    <motion.div
                      className="h-full"
                      style={{
                        background: 'linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)',
                        borderRadius: 999,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
                      }}
                      initial={{ width: '0%' }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span style={{ fontSize: 13, color: '#5A6B85' }}>{message}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0065F8' }}>
                      {progress}%
                    </span>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3"
                    style={{
                      background: '#FFF5F5',
                      border: '2px solid rgba(220,38,38,0.18)',
                      borderRadius: 14,
                    }}
                  >
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#B91C1C' }}>{error}</p>
                  </motion.div>
                )}

                {/* Success Message */}
                {status === 'complete' && progress === 100 && !error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center justify-center gap-2 p-3"
                    style={{
                      background: '#F0FBF5',
                      border: '2px solid rgba(16,185,129,0.22)',
                      borderRadius: 14,
                    }}
                  >
                    <svg
                      className="w-5 h-5"
                      style={{ color: '#10B981' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.4}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#0F9D6E' }}>
                      Video ready! Download starting...
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Close Button (only when not processing or on error) */}
              {(status === 'error' || (status === 'complete' && progress === 100)) && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={onClose}
                    className="transition active:scale-[0.97]"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: '#FFFFFF',
                      color: '#0065F8',
                      fontWeight: 700,
                      fontSize: 14,
                      border: '2px solid rgba(0,101,248,0.18)',
                      borderRadius: 999,
                      padding: '10px 26px',
                      boxShadow: '0px 3px 0px rgba(11,26,51,0.08)',
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default VideoWatermarkProgressModal;
